import React, { useState, useEffect, useCallback } from "react";
import {
  ThumbsUp,
  ThumbsDown,
  Star,
  AlertTriangle,
  Download,
  Filter,
  MessageSquare,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Eye,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { API_BASE_URL } from "../../constants";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

// ─── Helpers ───────────────────────────────────────────────────────────

const truncate = (str, max = 100) => {
  if (!str) return "";
  return str.length > max ? str.slice(0, max) + "..." : str;
};

const fmtDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const exportCSV = (rows) => {
  if (!rows || rows.length === 0) return;
  const headers = [
    "Date",
    "Question",
    "Response",
    "Rating",
    "Comment",
    "Session ID",
  ];
  const escape = (v) => {
    const s = String(v ?? "").replace(/"/g, '""');
    return `"${s}"`;
  };
  const csvRows = [
    headers.join(","),
    ...rows.map((r) =>
      [
        fmtDate(r.created_at),
        r.user_question,
        r.ai_response,
        r.rating,
        r.user_comment,
        r.session_id,
      ]
        .map(escape)
        .join(","),
    ),
  ];
  const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `feedback-export-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Sub-components ────────────────────────────────────────────────────

const SummaryCard = ({ label, value, icon: Icon, color, subtitle }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      {Icon && (
        <div className={`p-2 rounded-lg ${color || "bg-slate-100"}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
    <div className="text-2xl font-bold text-slate-800">{value}</div>
    {subtitle && (
      <span className="text-xs text-slate-400 mt-1">{subtitle}</span>
    )}
  </div>
);

const isPositive = (rating) =>
  rating === "positive" || rating === "thumbs_up" || rating === 1;

const isNegative = (rating) =>
  rating === "negative" || rating === "thumbs_down" || rating === -1;

const RatingIcon = ({ rating }) => {
  if (isPositive(rating)) {
    return <ThumbsUp className="w-4 h-4 text-green-600" />;
  }
  if (isNegative(rating)) {
    return <ThumbsDown className="w-4 h-4 text-red-500" />;
  }
  return <span className="text-xs text-slate-400">-</span>;
};

// ─── Main Component ────────────────────────────────────────────────────

const FeedbackDashboard = () => {
  const { token } = useAuth();

  // Data state
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [summary, setSummary] = useState(null);
  const [feedback, setFeedback] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ratingFilter, setRatingFilter] = useState("all");
  const [expandedRows, setExpandedRows] = useState(new Set());

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  // ─── Fetch facilities ──────────────────────────────────────────────

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const res = await fetch(`${AUTH_URL}/facilities`, { headers });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : [];
          setFacilities(list);
          if (list.length > 0 && !facilityId) {
            setFacilityId(list[0].facility_id || "");
          }
        }
      } catch (err) {
        console.error("Failed to load facilities:", err);
      }
    };
    fetchFacilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ─── Fetch feedback data ───────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);

    try {
      const [summaryRes, feedbackRes] = await Promise.all([
        fetch(
          `${API_BASE_URL}/get-feedback-summary?facility_id=${facilityId}`,
          { headers },
        ),
        fetch(
          `${API_BASE_URL}/get-feedback?facility_id=${facilityId}&limit=50`,
          { headers },
        ),
      ]);

      if (!summaryRes.ok) throw new Error("Failed to fetch feedback summary");
      if (!feedbackRes.ok) throw new Error("Failed to fetch feedback");

      const summaryData = await summaryRes.json();
      const feedbackData = await feedbackRes.json();

      // n8n returns an array — unwrap to get summary object
      setSummary(
        Array.isArray(summaryData) ? summaryData[0] || {} : summaryData,
      );
      setFeedback(Array.isArray(feedbackData) ? feedbackData : []);
    } catch (err) {
      console.error("Feedback fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Derived data ──────────────────────────────────────────────────

  const filteredFeedback = feedback.filter((item) => {
    if (ratingFilter === "positive") return isPositive(item.rating);
    if (ratingFilter === "negative") return isNegative(item.rating);
    return true;
  });

  const flaggedItems = feedback.filter((item) => isNegative(item.rating));

  const satisfactionColor = (pct) => {
    if (pct == null) return "text-slate-400";
    if (pct >= 80) return "text-green-600";
    if (pct >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const satisfactionBg = (pct) => {
    if (pct == null) return "bg-slate-100";
    if (pct >= 80) return "bg-green-500";
    if (pct >= 60) return "bg-yellow-500";
    return "bg-red-500";
  };

  const toggleExpanded = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center space-x-2">
            <MessageSquare className="w-5 h-5 text-slate-600" />
            <span>Feedback Dashboard</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            AI response quality from end-user ratings
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => exportCSV(filteredFeedback)}
            disabled={filteredFeedback.length === 0}
            className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Export CSV
          </button>
          <button
            onClick={fetchData}
            disabled={loading || !facilityId}
            className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw
              className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Facility Selector */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Facility
        </label>
        <select
          value={facilityId}
          onChange={(e) => setFacilityId(e.target.value)}
          className="w-full max-w-md px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-400 focus:border-transparent outline-none"
        >
          <option value="">Select a facility...</option>
          {facilities.map((f) => (
            <option key={f.facility_id} value={f.facility_id}>
              {f.display_name || f.facility_name || f.facility_id}
            </option>
          ))}
        </select>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">
              Failed to load feedback
            </p>
            <p className="text-sm text-red-600 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
          <span className="ml-2 text-sm text-slate-500">
            Loading feedback data...
          </span>
        </div>
      )}

      {/* Empty State (no facility selected) */}
      {!facilityId && !loading && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">
            Select a facility to view feedback data
          </p>
        </div>
      )}

      {/* Dashboard Content */}
      {facilityId && !loading && !error && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-500">
                  Satisfaction Rate
                </span>
                <div
                  className={`p-2 rounded-lg ${satisfactionBg(summary?.satisfaction_pct)}`}
                >
                  <Star className="w-4 h-4 text-white" />
                </div>
              </div>
              <div
                className={`text-3xl font-bold ${satisfactionColor(summary?.satisfaction_pct)}`}
              >
                {summary?.satisfaction_pct != null
                  ? `${summary.satisfaction_pct}%`
                  : "-"}
              </div>
              <span className="text-xs text-slate-400 mt-1">
                Based on all ratings
              </span>
            </div>

            <SummaryCard
              label="Total Feedback"
              value={summary?.total_feedback ?? 0}
              icon={MessageSquare}
              color="bg-slate-500"
              subtitle="All time responses rated"
            />

            <SummaryCard
              label="Thumbs Up"
              value={summary?.thumbs_up ?? 0}
              icon={ThumbsUp}
              color="bg-green-500"
              subtitle="Positive ratings"
            />

            <SummaryCard
              label="Thumbs Down"
              value={summary?.thumbs_down ?? 0}
              icon={ThumbsDown}
              color="bg-red-500"
              subtitle="Negative ratings"
            />
          </div>

          {/* Empty Feedback State */}
          {feedback.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">No feedback yet</p>
              <p className="text-slate-400 text-sm mt-1">
                Feedback buttons appear in the chat widget. Ratings will show
                here once users start providing feedback.
              </p>
            </div>
          )}

          {/* Recent Feedback Table */}
          {feedback.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-700">
                  Recent Feedback
                </h3>
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <select
                    value={ratingFilter}
                    onChange={(e) => setRatingFilter(e.target.value)}
                    className="text-sm border border-slate-300 rounded-lg px-2.5 py-1.5 focus:ring-2 focus:ring-slate-400 focus:border-transparent outline-none"
                  >
                    <option value="all">All Ratings</option>
                    <option value="positive">Positive Only</option>
                    <option value="negative">Negative Only</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-4 py-3 text-left font-medium text-slate-600">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">
                        Question
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">
                        Response
                      </th>
                      <th className="px-4 py-3 text-center font-medium text-slate-600">
                        Rating
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">
                        Comment
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredFeedback.map((item) => (
                      <tr
                        key={item.id}
                        className={
                          isNegative(item.rating)
                            ? "bg-red-50/60"
                            : "hover:bg-slate-50"
                        }
                      >
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          {fmtDate(item.created_at)}
                        </td>
                        <td
                          className="px-4 py-3 text-slate-700 max-w-xs"
                          title={item.user_question}
                        >
                          {truncate(item.user_question)}
                        </td>
                        <td
                          className="px-4 py-3 text-slate-500 max-w-xs"
                          title={item.ai_response}
                        >
                          {truncate(item.ai_response)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <RatingIcon rating={item.rating} />
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {item.user_comment || (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filteredFeedback.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-8 text-center text-sm text-slate-400"
                        >
                          No feedback matches the current filter
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Flagged Conversations */}
          {flaggedItems.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <h3 className="text-sm font-semibold text-slate-700">
                  Flagged Conversations
                </h3>
                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                  {flaggedItems.length}
                </span>
              </div>

              <div className="divide-y divide-slate-100">
                {flaggedItems.map((item) => {
                  const isOpen = expandedRows.has(item.id);
                  return (
                    <div key={item.id} className="bg-red-50/40">
                      <button
                        onClick={() => toggleExpanded(item.id)}
                        className="w-full px-5 py-3 flex items-center text-left hover:bg-red-50/80 transition-colors"
                      >
                        {isOpen ? (
                          <ChevronDown className="w-4 h-4 text-slate-400 mr-3 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-400 mr-3 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            <ThumbsDown className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                            <span className="text-sm font-medium text-slate-700 truncate">
                              {truncate(item.user_question, 80)}
                            </span>
                          </div>
                          <span className="text-xs text-slate-400 ml-6 mt-0.5 block">
                            {fmtDate(item.created_at)}
                            {item.user_comment && (
                              <span className="ml-3 text-red-500">
                                Comment: {truncate(item.user_comment, 60)}
                              </span>
                            )}
                          </span>
                        </div>
                        <Eye className="w-4 h-4 text-slate-400 ml-2 flex-shrink-0" />
                      </button>

                      {isOpen && (
                        <div className="px-5 pb-4 pl-12 space-y-3">
                          <div>
                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                              User Question
                            </label>
                            <p className="mt-1 text-sm text-slate-700 bg-white rounded-lg p-3 border border-slate-200">
                              {item.user_question || "-"}
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                              AI Response
                            </label>
                            <p className="mt-1 text-sm text-slate-600 bg-white rounded-lg p-3 border border-slate-200 whitespace-pre-wrap">
                              {item.ai_response || "-"}
                            </p>
                          </div>
                          {item.user_comment && (
                            <div>
                              <label className="text-xs font-semibold text-red-500 uppercase tracking-wider">
                                User Comment
                              </label>
                              <p className="mt-1 text-sm text-red-700 bg-red-50 rounded-lg p-3 border border-red-200">
                                {item.user_comment}
                              </p>
                            </div>
                          )}
                          <div className="text-xs text-slate-400">
                            Session: {item.session_id || "-"}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FeedbackDashboard;
